import { Link } from '@tanstack/react-router';
import type { CommandCenterKpis } from '@accounting/shared';
import { useCompanies } from '../../hooks/useCompanies';
import { useCommandCenterData } from '../../hooks/useCommandCenterData';
import {
  setCommandCenter,
  useCommandCenterStore,
} from '../../stores/useCommandCenterStore';
import { formatCurrency, formatPercent, todayIso } from '../../lib/formatters';
import { DateRangePicker } from '../reports/PeriodFilter';
import {
  CashPositionChart,
  BalanceSplitCharts,
  CompanySummaryTable,
  ExpenseBreakdownChart,
  NetIncomeTrendChart,
  RevenueTrendChart,
} from './Charts';

const yearStart = () => `${new Date().getFullYear()}-01-01`;

export function CommandCenter() {
  const { data: companies = [], isLoading: companiesLoading } = useCompanies();
  const store = useCommandCenterStore();

  const allIds = companies.map((c) => c.id);
  const effectiveIds =
    store.selectedCompanyIds.length > 0
      ? store.selectedCompanyIds.filter((id) => allIds.includes(id))
      : allIds;
  const from = store.from ?? yearStart();
  const to = store.to ?? todayIso();

  const cc = useCommandCenterData(effectiveIds, from, to);

  if (companiesLoading) return <p className="muted">Loading…</p>;

  if (companies.length === 0) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>Command Center</h1>
        <p className="muted">No companies yet. Create one to see cross-company KPIs.</p>
        <Link to="/companies" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
          Go to Companies
        </Link>
      </div>
    );
  }

  function toggleCompany(id: string) {
    const current = store.selectedCompanyIds.length > 0 ? store.selectedCompanyIds : allIds;
    const next = current.includes(id)
      ? current.filter((x) => x !== id)
      : [...current, id];
    // If the user re-selected everything, store empty (= "all").
    setCommandCenter({
      selectedCompanyIds: next.length === allIds.length ? [] : next,
    });
  }

  const isSelected = (id: string) =>
    store.selectedCompanyIds.length === 0 || store.selectedCompanyIds.includes(id);

  return (
    <div>
      <h1 style={{ fontSize: 24, margin: '0 0 16px' }}>Command Center</h1>

      {/* Filter bar */}
      <div
        className="card"
        style={{ padding: 12, marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {companies.map((c) => (
            <button
              key={c.id}
              onClick={() => toggleCompany(c.id)}
              className={isSelected(c.id) ? 'btn-primary' : 'btn-secondary'}
              style={{ fontSize: 12, padding: '4px 10px' }}
            >
              {c.name}
            </button>
          ))}
          {store.selectedCompanyIds.length > 0 && (
            <button
              className="btn-secondary"
              style={{ fontSize: 12, padding: '4px 10px' }}
              onClick={() => setCommandCenter({ selectedCompanyIds: [] })}
            >
              All
            </button>
          )}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 12, alignItems: 'center', fontSize: 13 }}>
          <DateRangePicker
            from={from}
            to={to}
            onChange={(r) => setCommandCenter({ from: r.from, to: r.to })}
          />
        </div>
      </div>

      {/* KPI grid */}
      {cc.kpis.data && <KPIGrid kpis={cc.kpis.data} />}

      {/* Charts */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
          gap: 16,
          marginTop: 16,
        }}
      >
        {cc.revenueTrend.data && <RevenueTrendChart data={cc.revenueTrend.data} />}
        {cc.netIncomeTrend.data && <NetIncomeTrendChart data={cc.netIncomeTrend.data} />}
        {cc.cashTrend.data && <CashPositionChart data={cc.cashTrend.data} />}
        {cc.expenseBreakdown.data && cc.expenseBreakdown.data.items.length > 0 && (
          <ExpenseBreakdownChart data={cc.expenseBreakdown.data} />
        )}
        {/* Balances, not flows — these two answer "where is it sitting", which
            none of the trend charts above can. */}
        {cc.balanceSplit.data && <BalanceSplitCharts data={cc.balanceSplit.data} />}
      </div>

      {/* Summary table */}
      <div style={{ marginTop: 16 }}>
        {cc.companySummary.data && <CompanySummaryTable data={cc.companySummary.data} />}
      </div>
    </div>
  );
}

function KPIGrid({ kpis }: { kpis: CommandCenterKpis }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: 12,
      }}
    >
      <KPITile label="Revenue" value={kpis.revenue} delta={kpis.deltas.revenue} goodWhenUp accent="#2E86AB" />
      <KPITile label="Expenses" value={kpis.expenses} delta={kpis.deltas.expenses} goodWhenUp={false} accent="#E8943A" />
      <KPITile label="Net Income" value={kpis.netIncome} delta={kpis.deltas.netIncome} goodWhenUp signed accent="#1A6B3A" />
      <KPITile label="Cash" value={kpis.cash} signed accent="#1B4965" />
      <KPITile label="Total Assets" value={kpis.totalAssets} signed accent="#8B2FC9" />
      <KPITile label="Total Liabilities" value={kpis.totalLiabilities} signed accent="#7A4F00" />
    </div>
  );
}

function KPITile({
  label,
  value,
  delta,
  goodWhenUp,
  signed,
  accent,
}: {
  label: string;
  value: string;
  delta?: number | null;
  goodWhenUp?: boolean;
  signed?: boolean;
  accent?: string;
}) {
  let deltaColor: string | undefined;
  if (delta != null && goodWhenUp !== undefined) {
    const isGood = goodWhenUp ? delta >= 0 : delta <= 0;
    deltaColor = isGood ? 'var(--ok)' : 'var(--bad)';
  }
  return (
    <div
      className="card"
      style={{ padding: 14, borderTop: `3px solid ${accent ?? 'var(--primary)'}` }}
    >
      <div className="muted" style={{ fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, marginTop: 4, color: 'var(--text-strong)' }}>
        {formatCurrency(value, signed)}
      </div>
      {delta != null && (
        <div style={{ fontSize: 12, marginTop: 2, color: deltaColor }}>
          {formatPercent(delta)} vs prior period
        </div>
      )}
    </div>
  );
}
